import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Shield, Calendar } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img 
              src="https://i.postimg.cc/mzV1rvRQ/logopareiad001.png" 
              alt="U-Clean" 
              className="h-8"
            />
            <span className="font-bold text-xl text-gray-800">U-Clean</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Política de Privacidade</h1>
          <p className="text-gray-500 flex items-center justify-center gap-2">
            <Calendar className="h-4 w-4" />
            Última atualização: Março de 2026
          </p>
        </div>

        {/* Sections */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8 prose prose-gray max-w-none">
            
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Compromisso com a Privacidade</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean valoriza a privacidade, a dignidade e a segurança de seus usuários. 
                Esta Política explica como coletamos, utilizamos, armazenamos e protegemos dados 
                pessoais no contexto da plataforma.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Dados que Podemos Coletar</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Podemos coletar:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>dados cadastrais, como nome, e-mail, telefone e foto de perfil;</li>
                <li>dados de acesso e autenticação;</li>
                <li>conteúdos publicados na plataforma;</li>
                <li>dados de uso, navegação, cliques, interações e preferências;</li>
                <li>informações técnicas do dispositivo, navegador, IP e logs;</li>
                <li>dados necessários para pagamentos, quando aplicável;</li>
                <li>informações fornecidas voluntariamente em formulários, suporte ou inscrição em cursos.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Finalidades do Uso dos Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Os dados poderão ser utilizados para:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>criar e administrar contas;</li>
                <li>permitir acesso a funcionalidades e conteúdos;</li>
                <li>personalizar a experiência do usuário;</li>
                <li>oferecer suporte e comunicação;</li>
                <li>garantir segurança, prevenção a fraudes e moderação;</li>
                <li>processar pagamentos e liberar acessos;</li>
                <li>gerar métricas e melhorar a plataforma;</li>
                <li>cumprir obrigações legais e regulatórias.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Base Legal</h2>
              <p className="text-gray-700 leading-relaxed">
                O tratamento de dados poderá ocorrer com base em consentimento, execução de contrato, 
                legítimo interesse, cumprimento de obrigação legal, exercício regular de direitos e 
                proteção da vida, conforme a legislação aplicável.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Compartilhamento de Dados</h2>
              <p className="text-gray-700 leading-relaxed mb-4">A U-Clean poderá compartilhar dados com:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>prestadores de serviços tecnológicos;</li>
                <li>processadores de pagamento;</li>
                <li>ferramentas de hospedagem, autenticação, analytics e suporte;</li>
                <li>autoridades públicas, quando houver obrigação legal.</li>
              </ul>
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 font-medium">
                  ℹ️ Não comercializamos dados pessoais de usuários como produto.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Armazenamento e Segurança</h2>
              <p className="text-gray-700 leading-relaxed">
                Adotamos medidas técnicas e administrativas para proteger os dados contra acesso 
                não autorizado, perda, destruição, alteração ou vazamento. Ainda assim, nenhum 
                ambiente digital é completamente livre de riscos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Direitos do Titular</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nos termos da LGPD, o usuário poderá solicitar, quando aplicável:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>confirmação da existência de tratamento;</li>
                <li>acesso aos dados;</li>
                <li>correção de dados incompletos, inexatos ou desatualizados;</li>
                <li>anonimização, bloqueio ou eliminação;</li>
                <li>portabilidade;</li>
                <li>informações sobre compartilhamento;</li>
                <li>revogação de consentimento.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Retenção de Dados</h2>
              <p className="text-gray-700 leading-relaxed">
                Os dados serão armazenados pelo tempo necessário para cumprir as finalidades da 
                plataforma, obrigações legais, segurança, defesa de direitos e funcionamento do serviço.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Cookies e Tecnologias Semelhantes</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean poderá utilizar cookies e tecnologias similares para autenticação, 
                desempenho, personalização, segurança e análise de uso.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Conteúdos Sensíveis e Contexto da Plataforma</h2>
              <p className="text-gray-700 leading-relaxed">
                Como a plataforma pode envolver temas relacionados à saúde, recuperação, comportamento 
                e vulnerabilidade emocional, a U-Clean adota especial cuidado com o tratamento dessas 
                informações. O usuário deve evitar publicar dados excessivamente sensíveis em áreas 
                públicas da comunidade.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Transferência Internacional</h2>
              <p className="text-gray-700 leading-relaxed">
                Caso haja uso de infraestrutura tecnológica hospedada no exterior, a U-Clean adotará 
                medidas razoáveis para proteger os dados pessoais, conforme a legislação aplicável.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Atualizações desta Política</h2>
              <p className="text-gray-700 leading-relaxed">
                Esta Política poderá ser atualizada periodicamente. A versão vigente será sempre 
                a publicada na plataforma.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Contato e Solicitações de Privacidade</h2>
              <p className="text-gray-700 leading-relaxed">
                Solicitações sobre dados pessoais e privacidade poderão ser feitas pelos canais 
                oficiais disponibilizados pela U-Clean.
              </p>
            </section>

          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <Link to="/termos">
            <Button variant="outline">Termos de Uso</Button>
          </Link>
          <Link to="/aviso-importante">
            <Button variant="outline">Aviso Importante</Button>
          </Link>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-6 mt-8">
        <div className="container max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} U-Clean. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicyPage;
